import { ReactComponent as BindBoxIcon } from '@/assets/images/svg/bindbox.svg';
import Button from '@/components/Button';
import PadItemSkeleton from '@/components/Skeleton/PadItemSkeleton';
import { getServerApi } from '@/gql';
import type { LazbubuListQuery } from '@/gql/graph/__generated__/request';
import { LazbubuIncubator, WhitelistInfoPrompt } from '@/pages/UI';
import user from '@/stores/user';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { LazbubuCard } from './LazbubuCard';
// Main page container with responsive design
const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-height: calc(100vh - 80px);
    padding: 80px 16px 40px;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

// Container for blind box section
const BlindBoxContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 32px;
    margin: 60px 0;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 20px;
        margin: 40px 0;
    }
`;

// Individual box wrapper
const BoxWrapper = styled.div`
    width: 270px;
    background-color: #fff;
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }

    @media (max-width: 480px) {
        width: 100%;
        max-width: 300px;
    }
`;

// Box image section
const BoxImage = styled.div<{ src: string }>`
    width: 100%;
    height: 180px;
    background-image: url(${(props) => props.src});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
`;

// Box content section
const BoxContent = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

// Box title
const BoxTitle = styled.h3`
    font-size: 14px;
    color: #333;
    text-align: center;
    margin: 0;
    line-height: 1.4;
`;

// Styled button for boxes
const BoxButton = styled(Button)`
    width: 100% !important;
    height: 40px !important;
    background-color: #000 !important;
    color: #fff !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
`;

// Cards grid container
const CardsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 24px 0;
`;

// Cards grid
const CardsGrid = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;

    max-width: 900px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        flex-direction: column;
        gap: 16px;
        max-width: 100%;
    `};
`;

// Loading skeleton container
const SkeletonContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 24px;
    justify-content: center;
    align-items: center;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        flex-direction: column;
        gap: 16px;
    `};
`;

// Empty state container
const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 16px;
    padding: 40px 20px;
    max-width: 400px;
`;

// Empty state title
const EmptyStateTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;

    @media (max-width: 480px) {
        font-size: 16px;
    }
`;

// Empty state description
const EmptyStateDescription = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
`;

const NoResultWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 40px;

    svg {
        color: rgba(207, 223, 249, 1);
        width: 64px;
        height: 64px;
    }
`;

const MyLazbubu: React.FC = observer(() => {
    const history = useHistory();

    const isOpenBox = true;

    const [loading, setLoading] = useState<boolean>(false);
    const [items, setItems] = useState<LazbubuListQuery['lazbubuList']>([]);
    const [lostItems, setLostItems] = useState<LazbubuListQuery['lazbubuList']>(
        [],
    );

    async function getLazbubuList() {
        setLoading(true);
        const res = await getServerApi().getLazbubuList();
        try {
            const list = res.lazbubuList;
            const lostList = res?.lostLazbubuList || [];

            setItems(list.sort((a, b) => b.birthday - a.birthday));
            setLostItems(lostList.sort((a, b) => b.birthday - a.birthday));
        } catch (e) {
            console.error(e);
        }

        setLoading(false);
    }

    const showBox = user.canOpenBlindBox || user.canOpenGiftBox;

    useEffect(() => {
        if (user.connected && user.userDetail?.id) {
            getLazbubuList();
        } else {
            setItems([]);
            setLostItems([]);
        }
    }, [user.userDetail?.id, user.connected]);

    return (
        <PageContainer>
            <LazbubuIncubator />
            {!!user?.userDetail?.id &&
                !user.hasBlindBox &&
                !loading &&
                !lostItems.length &&
                !items.length && (
                    <>
                        <NoResultWrapper>
                            <BindBoxIcon />
                        </NoResultWrapper>
                        <WhitelistInfoPrompt />
                    </>
                )}{' '}
            {showBox && (
                <BlindBoxContainer>
                    {user.canOpenBlindBox && (
                        <BoxWrapper>
                            <BoxImage
                                src={require('@/assets/images/bindbox.png')}
                            />
                            <BoxContent>
                                <BoxTitle>
                                    Looks like you've got one blind box
                                </BoxTitle>
                                <BoxButton
                                    onClick={() => {
                                        history.push('/blind-box');
                                    }}
                                >
                                    Open box
                                </BoxButton>
                            </BoxContent>
                        </BoxWrapper>
                    )}
                    {user.canOpenGiftBox && (
                        <BoxWrapper>
                            <BoxImage
                                src={require('@/assets/images/gift.png')}
                            />
                            <BoxContent>
                                <BoxTitle>
                                    Looks like you've got one gift box
                                </BoxTitle>
                                <BoxButton
                                    onClick={() => {
                                        history.push('/gift');
                                    }}
                                >
                                    Open gift
                                </BoxButton>
                            </BoxContent>
                        </BoxWrapper>
                    )}
                </BlindBoxContainer>
            )}
            {!!user?.userDetail?.id ? (
                <CardsContainer>
                    {loading ? (
                        <SkeletonContainer>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <PadItemSkeleton key={i} />
                            ))}
                        </SkeletonContainer>
                    ) : items.length > 0 || lostItems.length > 0 ? (
                        <CardsGrid>
                            {items.map((lazbubu) => (
                                <LazbubuCard
                                    key={String(lazbubu.tokenId)}
                                    data={lazbubu}
                                    onTransfer={getLazbubuList}
                                />
                            ))}

                            {lostItems.map((lazbubu) => (
                                <LazbubuCard
                                    key={String(lazbubu.tokenId)}
                                    data={lazbubu}
                                    isTransfered
                                />
                            ))}
                        </CardsGrid>
                    ) : null}
                </CardsContainer>
            ) : (
                <CardsContainer>
                    <Button auth size="small">
                        Connect Wallet
                    </Button>
                </CardsContainer>
            )}
        </PageContainer>
    );
});

export default MyLazbubu;
